import { useCallback } from "react";
import { useSelector } from "react-redux";
import {
  hasModuleAccess as hasModuleAccessPure,
  isAdminRole,
} from "../constants/moduleScopes";

/**
 * Per-module access hook.
 *
 * Reads the active workspace's role + product_scopes from the live model
 * (`state.auth.userData.id_client[state.auth.id_client]`) and exposes a
 * `canAccess(moduleKey)` predicate. Only the super_admin role bypasses
 * module scopes. Missing scopes deny access for every other role.
 *
 * Used by the sidebar nav (which items to show) and the route guard (which
 * routes to allow). DashAI is never gated by this hook.
 *
 * @returns {{
 *   canAccess: (moduleKey: string) => boolean,
 *   productScopes: Record<string, string[]> | null,
 *   role: string | null,
 *   isAdmin: boolean,
 * }}
 */
export const useModuleAccess = () => {
  const productScopes = useSelector((state) => {
    const workspaceKey = state.auth?.id_client;
    return (
      state.auth?.userData?.id_client?.[workspaceKey]?.product_scopes ?? null
    );
  });
  const role = useSelector((state) => {
    const workspaceKey = state.auth?.id_client;
    return state.auth?.userData?.id_client?.[workspaceKey]?.role ?? null;
  });

  const canAccess = useCallback(
    (moduleKey) => hasModuleAccessPure(productScopes, role, moduleKey),
    [productScopes, role]
  );

  return {
    canAccess,
    productScopes,
    role,
    isAdmin: isAdminRole(role),
  };
};

export default useModuleAccess;
